import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { apiFetch } from '@/lib/api';
import { notify } from '@/lib/notify';
import {
  getImpersonationMeta,
  isImpersonating,
  stopImpersonationSession,
  updateImpersonationToken,
} from '@/lib/impersonation';

export function ImpersonationBanner() {
  const [meta, setMeta] = useState(() => getImpersonationMeta());
  const [busy, setBusy] = useState(false);

  if (!isImpersonating() || !meta) {
    return null;
  }

  const displayName =
    [meta.targetUser.firstName, meta.targetUser.lastName].filter(Boolean).join(' ') ||
    meta.targetUser.email;
  const rolesLabel = meta.targetUser.roles?.length
    ? meta.targetUser.roles.join(', ')
    : 'user';

  async function handleStop() {
    setBusy(true);
    try {
      await apiFetch('/api/v1/platform/impersonate/stop', { method: 'POST' });
    } catch {
      // Still exit locally even if audit call fails
    } finally {
      const { restored } = stopImpersonationSession();
      setBusy(false);
      window.location.href = restored ? '/platform' : '/login';
    }
  }

  async function handleWritesToggle() {
    const enabled = !meta!.writesEnabled;
    setBusy(true);
    try {
      const res = await apiFetch('/api/v1/platform/impersonate/writes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        notify.error(data.error || 'Failed to update write mode');
        return;
      }
      const result = await res.json();
      updateImpersonationToken(result);
      setMeta(getImpersonationMeta());
      notify.success(enabled ? 'Write mode enabled' : 'Write mode disabled (read-only)');
    } catch {
      notify.error('Failed to update write mode');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      role="status"
      className="sticky top-0 z-50 flex flex-wrap items-center justify-between gap-3 border-b border-neutral-800 bg-neutral-950 px-4 py-2.5 text-neutral-50"
    >
      <div className="flex min-w-0 flex-wrap items-center gap-2.5 text-sm">
        <Badge
          className={
            meta.writesEnabled
              ? 'border-transparent bg-amber-400 text-neutral-950 hover:bg-amber-400'
              : 'border-transparent bg-neutral-100 text-neutral-950 hover:bg-neutral-100'
          }
        >
          {meta.writesEnabled ? 'Writes enabled' : 'Read-only'}
        </Badge>
        <span className="min-w-0 text-neutral-200">
          Viewing as{' '}
          <span className="font-medium text-white">{displayName}</span>
          <span className="text-neutral-400"> · {meta.targetUser.email}</span>
          <span className="text-neutral-500"> · {rolesLabel}</span>
        </span>
      </div>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          disabled={busy}
          onClick={handleWritesToggle}
          className="border-neutral-600 bg-transparent text-neutral-50 hover:bg-neutral-800 hover:text-white"
        >
          {meta.writesEnabled ? 'Disable writes' : 'Enable writes'}
        </Button>
        <Button
          size="sm"
          disabled={busy}
          onClick={handleStop}
          className="bg-white text-neutral-950 hover:bg-neutral-200"
        >
          Stop impersonating
        </Button>
      </div>
    </div>
  );
}

export default ImpersonationBanner;
